import { useState, useEffect } from 'react';
var hasOwnProperty = Object.prototype.hasOwnProperty;

function is(x, y) {
  if (x === y) {
    return x !== 0 || y !== 0 || 1 / x === 1 / y;
  } else {
    return x !== x && y !== y;
  }
}

function shallowEqual(objA, objB) {
  if (is(objA, objB)) {
    return true;
  }

  if (typeof objA !== 'object' || objA === null || typeof objB !== 'object' || objB === null) {
    return false;
  }

  var keysA = Object.keys(objA);
  var keysB = Object.keys(objB);

  if (keysA.length !== keysB.length) {
    return false;
  }

  for (var i = 0; i < keysA.length; i++) {
    if (!hasOwnProperty.call(objB, keysA[i]) || !is(objA[keysA[i]], objB[keysA[i]])) {
      return false;
    }
  }

  return true;
}

var GlobalState =
/** @class */
function () {
  function GlobalState(initState) {
    this.connectedItems = {};
    this.connectedHooks = {};

    this.defaultMapState = function (gs) {
      return gs;
    };

    this.state = initState;
  }

  GlobalState.prototype.setState = function (newStatePatch) {
    var newState = Object.assign({}, this.state, newStatePatch);
    if (shallowEqual(this.state, newState)) return;
    this.state = newState;
    this.notifyStateChanged();
  };

  GlobalState.prototype.connect = function (target, mapState) {
    var connectedItem = new ConnectedItem(target, mapState, this.state);
    this.connectedItems[connectedItem.id] = connectedItem;
    this.interceptUnmountToDisconnect(connectedItem);
    return connectedItem.mappedState;
  };

  GlobalState.prototype.useGlobalState = function (mapState) {
    var _this = this;

    var mapStateFn = mapState || this.defaultMapState;

    var _a = useState(mapStateFn(this.state)),
        mappedState = _a[0],
        setMappedState = _a[1];

    var _b = useState(function () {
      return new ConnectedHook(mappedState, mapStateFn, setMappedState);
    }),
        connectedHook = _b[0],
        setConnectedHook = _b[1];

    useEffect(function () {
      _this.connectedHooks[connectedHook.id] = connectedHook;
      return function () {
        delete _this.connectedHooks[connectedHook.id];
      };
    }, []);
    return mappedState;
  };

  GlobalState.prototype.interceptUnmountToDisconnect = function (item) {
    var _this = this;

    var prevUnmount = item.target.componentWillUnmount;

    item.target.componentWillUnmount = function () {
      _this.disconnect(item);

      prevUnmount && prevUnmount.call(item.target);
    };
  };

  GlobalState.prototype.disconnect = function (item) {
    delete this.connectedItems[item.id];
  };

  GlobalState.prototype.notifyStateChanged = function () {
    for (var key in this.connectedItems) {
      this.connectedItems[key].processNewGlobaleState(this.state);
    }

    for (var key in this.connectedHooks) {
      this.connectedHooks[key].processNewGlobaleState(this.state);
    }
  };

  return GlobalState;
}();

var connectedItemIdGen = 1;

var ConnectedHook =
/** @class */
function () {
  function ConnectedHook(mappedState, mapStateFn, setMappedStateFn) {
    this.mappedState = mappedState;
    this.mapStateFn = mapStateFn;
    this.setMappedStateFn = setMappedStateFn;
    this.id = connectedItemIdGen++;
  }

  ConnectedHook.prototype.processNewGlobaleState = function (gs) {
    try {
      var newMappedState = this.evaluateMapState(gs);
      if (shallowEqual(this.mappedState, newMappedState)) return;
      this.mappedState = newMappedState;
      this.setMappedStateFn(this.mappedState);
    } catch (e) {
      console.error('Error during processing new global state');
      console.error(e);
    }
  };

  ConnectedHook.prototype.evaluateMapState = function (gs) {
    try {
      return this.mapStateFn(gs);
    } catch (e) {
      console.error('Error in map global state function for hook ');
      console.error(e);
      throw e;
    }
  };

  return ConnectedHook;
}();

var ConnectedItem =
/** @class */
function () {
  function ConnectedItem(target, mapStateFn, gs) {
    this.target = target;
    this.mapStateFn = mapStateFn;
    this.id = connectedItemIdGen++;
    this.mappedState = mapStateFn(gs);
  }

  ConnectedItem.prototype.processNewGlobaleState = function (gs) {
    var newMappedState = this.evaluateMapState(gs);
    if (!newMappedState) return;
    if (shallowEqual(this.mappedState, newMappedState)) return;
    this.mappedState = newMappedState;
    this.target.setState(this.mappedState);
  };

  ConnectedItem.prototype.evaluateMapState = function (gs) {
    try {
      return this.mapStateFn(gs);
    } catch (e) {
      console.error('Error in map global state function for component ' + this.target.constructor.name);
      console.error(e);
      return undefined;
    }
  };

  return ConnectedItem;
}();

export { GlobalState };